import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useGlobalContext } from "../../data/GlobalContext";
import GameImage from "../GameImage";
import { launchGameByName } from "../../data/launchGame";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faSearch } from "@fortawesome/free-solid-svg-icons";

export default function JiliGames({ onClose, onBack }) {
	const location = useLocation();

	const { allGamesFlat, username, tokenUrl, launchUrl, gamesLoading } =
		useGlobalContext();

	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

	// Get all JILI games
	const HIDDEN_JILI_GAMES = [
		"boom legend",
		"boom legend [jp]",
		"mega fishing",
		"happy fishing",
		"bombing fishing",
		"bombing fishing [jp]",
		"happy fishing [jp]",
		"jackpot fishing",
		"jackpot fishing 2",
		"mega fishing [jp]",
		"all-star fishing",
		"royal fishing",
		"royal fishing [jp]",

		// Fishing Games
		"dinosaur tycoon",
		"dinosaur tycoon [jp]",
		"dinosaur tycoon ii [jp]",
		"dragon fortune",
		"dragon fortune [jp]",
		"dinosaur tycoon ii",
		"ocean king jackpot",
		"ocean king jackpot [jp]",
		"fortune king jackpot",
		"fortune zombie",
		"circus jackpot",

		// Card / Table Games
		"teenpatti",
		"ak47",
		"andar bahar",
		"rummy",
		"callbreak",
		"teenpatti joker",
		"callbreak quick",
		"teenpatti 20-20",
		"ludo quick",
		"tongits go",
		"pusoy go",
		"blackjack",
		"blackjack lucky ladies",
		"mini flush",
		"pool rummy",
		"caribbean stud poker",
		"video poker",
		"ultimate texas holdem",
		"domino go",

		// Additional Hidden JILI Games
		"number king",
		"poker king",
		"dragon & tiger",
		"irich bingo",
		"7up7down",
		"baccarat",
		"fortune bingo",
		"sic bo",
		"super bingo",
		"bingo carnaval",
		"win drop",
		"lucky bingo",
		"jackpot bingo",
		"color game",
		"go goal bingo",
		"calaca bingo",
		"pappu",
		"west hunter bingo",
		"bingo adventure",
		"keno",
		"candyland bingo",
		"color prediction",
		"magic lamp bingo",
		"pearls of bingo",
		"european roulette",
		"jogo do bicho",
		"speed baccarat",
		"fish prawn crab",
		"super ace scratch",
		"jhandi munda",
		"boxing extravaganza",
		"thai hilo",
		"super e-sabong (cockfight)",
		"keno bonus number",
		"cricket roulette",
		"fortune roulette",
		"go for champion",
		"fortune gems scratch",
		"cricket war",
		"keno super chance",
		"keno extra bet",
		"penalty kicks",
		"golden treasure",
		"color game extreme",
		"chicken dash",
		"drop ball",
		"frog dash",
		"royal quest",
		"gallina fortunata",
		"golden treasure lightning",
		"chicken dash 10000",
		"amar akbar anthony",
		"big small casino royale",
		"speed andarbahar",
		"grand golden treasure",
		"go rush",
		"call break",
		"mines",
		"tower",
		"hilo",
		"limbo",
		"wheel",
		"mines gold",
		"plinko",
		"crash bonus",
		"crash goal",
		"crash cricket",
		"crash touchdown",
		"mines grand",
		"crash puck",
		"plinko of mine",
		"cash stack",
		"mines cricket",
		"lucky roulette",
		"crash trader",
		"crash cricket legend",
		"crash fruit",
		"football golden cup",
		"mines football",
		"plinko football",
		"jackpot football",
		"cash balloon",
		"thor dice",
		"chicken dash jackpot",
		"super e-sabong",
		"sicbo",
		"poolrummy",
	];

	const jiliGames = useMemo(() => {
		return allGamesFlat
			.filter((game) => {
				const provider = game.provider?.trim().toLowerCase() || "";
				const gameName = game.gameName?.trim().toLowerCase() || "";

				if (provider !== "jili") return false;

				if (HIDDEN_JILI_GAMES.includes(gameName)) {
					return false;
				}

				return true;
			})
			.sort((a, b) => {
				const aName = a.gameName?.trim().toLowerCase();
				const bName = b.gameName?.trim().toLowerCase();

				if (aName === "super ace") return -1;
				if (bName === "super ace") return 1;

				return 0;
			});
	}, [allGamesFlat]);

	// Filter JILI games based on search
	const filteredJiliGames = useMemo(() => {
		const keyword = debouncedSearchTerm.trim().toLowerCase();

		if (!keyword) {
			return jiliGames;
		}

		return jiliGames.filter((game) => {
			const gameName = game.gameName?.toLowerCase() || "";
			const provider = game.provider?.toLowerCase() || "";
			const gameId = String(game.gameId || "").toLowerCase();

			return (
				gameName.includes(keyword) ||
				provider.includes(keyword) ||
				gameId.includes(keyword)
			);
		});
	}, [jiliGames, debouncedSearchTerm]);

	const playGame = async (game) => {
		if (!username) {
			alert("Please login to play.");
			return;
		}

		await launchGameByName({
			provider: game.provider,
			gameName: game.gameName,
			username,
			tokenUrl,
			launchUrl,
			allGamesFlat,
			location,
		});
	};

	useEffect(() => {
		// Clear immediately
		if (!searchTerm.trim()) {
			setDebouncedSearchTerm("");
			return;
		}

		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
		}, 300);

		return () => clearTimeout(timer);
	}, [searchTerm]);

	if (gamesLoading) {
		return (
			<section className="jili-games-page">
				<div className="jili-games-container">
					<div className="jili-games-grid">
						{Array.from({ length: 12 }).map((_, index) => (
							<div className="jili-game-card skeleton-card" key={index}>
								<div className="jili-game-image skeleton-image" />

								<div className="jili-game-content">
									<div className="skeleton-line skeleton-title" />
									<div className="skeleton-line skeleton-provider" />
									<div className="skeleton-button" />
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="jili-games-page">
			<div className="jili-games-container">
				<div className="jili-games-header">
					{/* Search */}
					<div className="jili-search-wrapper">
						<div className="jili-search-box">
							<span className="jili-search-icon">
								<FontAwesomeIcon icon={faSearch} />
							</span>

							<input
								type="text"
								className="jili-search-input"
								placeholder="Search JILI games..."
								value={searchTerm}
								onChange={(event) => setSearchTerm(event.target.value)}
							/>

							{searchTerm && (
								<button
									type="button"
									className="jili-search-clear"
									onClick={() => setSearchTerm("")}
									aria-label="Clear search"
								>
									×
								</button>
							)}
						</div>
					</div>
				</div>

				{/* Games */}
				{filteredJiliGames.length > 0 ? (
					<div className="jili-games-grid">
						{filteredJiliGames.map((game) => (
							<article className="jili-game-card" key={game.gameId}>
								<div className="jili-card-shine" />

								<div className="jili-game-image" onClick={() => playGame(game)}>
									<GameImage
										gameId={game.gameId}
										gameImg={
											game.gameImg || game.image || game.imageUrl || game.img
										}
										gameName={game.gameName}
									/>

									<div className="jili-image-overlay">
										<button
											type="button"
											className="jili-image-play"
											onClick={(event) => {
												event.stopPropagation();
												playGame(game);
											}}
										>
											▶
										</button>
									</div>

									<span className="jili-provider-tag">JILI</span>
								</div>

								<div className="jili-game-content">
									<div className="jili-game-info">
										<h3 className="jili-game-name">{game.gameName}</h3>

										<span className="jili-game-provider">JILI</span>
									</div>
								</div>
							</article>
						))}
					</div>
				) : debouncedSearchTerm ? (
					/* No Search Result */
					<div className="jili-empty-state">
						<div className="jili-empty-icon">🔍</div>

						<h3>No games found</h3>
						<p>No JILI games match "{debouncedSearchTerm}".</p>

						<button
							type="button"
							className="jili-clear-search-btn"
							onClick={() => setSearchTerm("")}
						>
							Clear Search
						</button>
					</div>
				) : (
					/* No JILI Games */
					<div className="jili-empty-state">
						<div className="jili-empty-icon">🍭</div>

						<h3>No JILI games available</h3>

						<p>Please check again later.</p>
					</div>
				)}
			</div>
		</section>
	);
}
